import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { MapPin } from 'lucide-react';

const PROVIDER_COLORS = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

// Mapping des noms de région lisibles vers les codes de région
const REGION_NAME_TO_CODE = {
  // Azure display names
  'eastus': 'eastus',
  'eastus2': 'eastus2',
  'useast': 'eastus',
  'useast2': 'eastus2',
  'us east': 'eastus',
  'us east 2': 'eastus2',
  'westus': 'westus',
  'westus2': 'westus2',
  'westus3': 'westus3',
  'uswest': 'westus',
  'uswest2': 'westus2',
  'uswest3': 'westus3',
  'us west': 'westus',
  'us west 2': 'westus2',
  'us west 3': 'westus3',
  'centralus': 'centralus',
  'uscent': 'centralus',
  'us central': 'centralus',
  'northcentralus': 'northcentralus',
  'usnorthcentral': 'northcentralus',
  'us north central': 'northcentralus',
  'southcentralus': 'southcentralus',
  'ussouthcentral': 'southcentralus',
  'us south central': 'southcentralus',
  'northeurope': 'northeurope',
  'eunorth': 'northeurope',
  'eu north': 'northeurope',
  'north europe': 'northeurope',
  'europe north': 'northeurope',
  'westeurope': 'westeurope',
  'euwest': 'westeurope',
  'eu west': 'westeurope',
  'west europe': 'westeurope',
  'europe west': 'westeurope',
  'francecentral': 'francecentral',
  'francesouth': 'francesouth',
  'france central': 'francecentral',
  'france south': 'francesouth',
  'uksouth': 'uksouth',
  'ukwest': 'ukwest',
  'uk south': 'uksouth',
  'uk west': 'ukwest',
  'germanywestcentral': 'germanywestcentral',
  'germany west central': 'germanywestcentral',
  'switzerlandnorth': 'switzerlandnorth',
  'switzerland north': 'switzerlandnorth',
  'norwayeast': 'norwayeast',
  'norway east': 'norwayeast',
  'swedencentral': 'swedencentral',
  'sweden central': 'swedencentral',
  'southeastasia': 'southeastasia',
  'asiasoutheast': 'southeastasia',
  'asia southeast': 'southeastasia',
  'southeast asia': 'southeastasia',
  'eastasia': 'eastasia',
  'asiaeast': 'eastasia',
  'asia east': 'eastasia',
  'east asia': 'eastasia',
  'australiaeast': 'australiaeast',
  'australia east': 'australiaeast',
  'australiasoutheast': 'australiasoutheast',
  'australia southeast': 'australiasoutheast',
  'japaneast': 'japaneast',
  'japan east': 'japaneast',
  'japanwest': 'japanwest',
  'japan west': 'japanwest',
  'koreacentral': 'koreacentral',
  'korea central': 'koreacentral',
  'koreasouth': 'koreasouth',
  'korea south': 'koreasouth',
  'southindia': 'southindia',
  'south india': 'southindia',
  'centralindia': 'centralindia',
  'central india': 'centralindia',
  'westindia': 'westindia',
  'west india': 'westindia',
  'canadacentral': 'canadacentral',
  'canada central': 'canadacentral',
  'canadaeast': 'canadaeast',
  'canada east': 'canadaeast',
  'brazilsouth': 'brazilsouth',
  'brazil south': 'brazilsouth',
  'southafricanorth': 'southafricanorth',
  'south africa north': 'southafricanorth',
  'uaenorth': 'uaenorth',
  'uae north': 'uaenorth',
};

// Mapping des régions cloud vers coordonnées géographiques
const REGION_COORDS = {
  // Azure
  'eastus': { lat: 37.3719, lon: -79.8164, label: 'East US' },
  'eastus2': { lat: 36.6681, lon: -78.3889, label: 'East US 2' },
  'westus': { lat: 37.783, lon: -122.417, label: 'West US' },
  'westus2': { lat: 47.233, lon: -119.852, label: 'West US 2' },
  'westus3': { lat: 33.717, lon: -117.828, label: 'West US 3' },
  'centralus': { lat: 41.5908, lon: -93.6208, label: 'Central US' },
  'northcentralus': { lat: 41.8819, lon: -87.6278, label: 'North Central US' },
  'southcentralus': { lat: 29.4167, lon: -98.5, label: 'South Central US' },
  'westcentralus': { lat: 40.890, lon: -110.234, label: 'West Central US' },
  'northeurope': { lat: 53.3478, lon: -6.2597, label: 'North Europe' },
  'westeurope': { lat: 52.3667, lon: 4.9, label: 'West Europe' },
  'francecentral': { lat: 46.3772, lon: 2.3730, label: 'France Central' },
  'francesouth': { lat: 43.8345, lon: 2.1972, label: 'France South' },
  'uksouth': { lat: 50.941, lon: -0.799, label: 'UK South' },
  'ukwest': { lat: 53.427, lon: -3.084, label: 'UK West' },
  'germanywestcentral': { lat: 50.110924, lon: 8.682127, label: 'Germany West Central' },
  'switzerlandnorth': { lat: 47.451542, lon: 8.564572, label: 'Switzerland North' },
  'norwayeast': { lat: 59.913868, lon: 10.752245, label: 'Norway East' },
  'swedencentral': { lat: 60.128161, lon: 18.643501, label: 'Sweden Central' },
  'southeastasia': { lat: 1.283, lon: 103.833, label: 'Southeast Asia' },
  'eastasia': { lat: 22.267, lon: 114.188, label: 'East Asia' },
  'australiaeast': { lat: -33.86, lon: 151.2094, label: 'Australia East' },
  'australiasoutheast': { lat: -37.8136, lon: 144.9631, label: 'Australia Southeast' },
  'japaneast': { lat: 35.68, lon: 139.77, label: 'Japan East' },
  'japanwest': { lat: 34.6939, lon: 135.5022, label: 'Japan West' },
  'koreacentral': { lat: 37.5665, lon: 126.978, label: 'Korea Central' },
  'koreasouth': { lat: 35.1796, lon: 129.0756, label: 'Korea South' },
  'southindia': { lat: 12.9822, lon: 80.1636, label: 'South India' },
  'centralindia': { lat: 18.5822, lon: 73.9197, label: 'Central India' },
  'westindia': { lat: 19.088, lon: 72.868, label: 'West India' },
  'canadacentral': { lat: 43.653, lon: -79.383, label: 'Canada Central' },
  'canadaeast': { lat: 46.817, lon: -71.217, label: 'Canada East' },
  'brazilsouth': { lat: -23.55, lon: -46.633, label: 'Brazil South' },
  'southafricanorth': { lat: -25.731340, lon: 28.218370, label: 'South Africa North' },
  'uaenorth': { lat: 25.266666, lon: 55.316666, label: 'UAE North' },

  // AWS
  'us-east-1': { lat: 37.3719, lon: -79.8164, label: 'US East (N. Virginia)' },
  'us-east-2': { lat: 40.4173, lon: -82.9071, label: 'US East (Ohio)' },
  'us-west-1': { lat: 37.3541, lon: -121.9552, label: 'US West (N. California)' },
  'us-west-2': { lat: 45.5234, lon: -122.6762, label: 'US West (Oregon)' },
  'eu-west-1': { lat: 53.3498, lon: -6.2603, label: 'EU (Ireland)' },
  'eu-west-2': { lat: 51.5074, lon: -0.1278, label: 'EU (London)' },
  'eu-west-3': { lat: 48.8566, lon: 2.3522, label: 'EU (Paris)' },
  'eu-central-1': { lat: 50.1109, lon: 8.6821, label: 'EU (Frankfurt)' },
  'eu-north-1': { lat: 59.3293, lon: 18.0686, label: 'EU (Stockholm)' },
  'ap-southeast-1': { lat: 1.3521, lon: 103.8198, label: 'Asia Pacific (Singapore)' },
  'ap-southeast-2': { lat: -33.8688, lon: 151.2093, label: 'Asia Pacific (Sydney)' },
  'ap-northeast-1': { lat: 35.6895, lon: 139.6917, label: 'Asia Pacific (Tokyo)' },
  'ap-northeast-2': { lat: 37.5665, lon: 126.9780, label: 'Asia Pacific (Seoul)' },
  'ap-south-1': { lat: 19.0760, lon: 72.8777, label: 'Asia Pacific (Mumbai)' },
  'sa-east-1': { lat: -23.5505, lon: -46.6333, label: 'South America (São Paulo)' },
  'ca-central-1': { lat: 45.5017, lon: -73.5673, label: 'Canada (Central)' },

  // GCP
  'us-central1': { lat: 41.2619, lon: -95.8608, label: 'Iowa' },
  'us-east1': { lat: 33.7490, lon: -84.3880, label: 'South Carolina' },
  'us-west1': { lat: 45.5945, lon: -121.1786, label: 'Oregon' },
  'europe-west1': { lat: 50.4501, lon: 3.8196, label: 'Belgium' },
  'europe-west4': { lat: 53.4386, lon: 6.8355, label: 'Netherlands' },
  'europe-west9': { lat: 48.8566, lon: 2.3522, label: 'Paris' },
  'asia-northeast1': { lat: 35.6895, lon: 139.6917, label: 'Tokyo' },
  'asia-southeast1': { lat: 1.3521, lon: 103.8198, label: 'Singapore' },

  // OVH - principales régions
  'gra': { lat: 50.7467, lon: 3.2035, label: 'Gravelines (FR)' },
  'sbg': { lat: 48.5734, lon: 7.7521, label: 'Strasbourg (FR)' },
  'rbx': { lat: 50.6333, lon: 3.0667, label: 'Roubaix (FR)' },
  'bhs': { lat: 45.5017, lon: -73.5673, label: 'Beauharnois (CA)' },
  'waw': { lat: 52.2297, lon: 21.0122, label: 'Warsaw (PL)' },
  'de': { lat: 50.1109, lon: 8.6821, label: 'Frankfurt (DE)' },
  'uk': { lat: 51.5074, lon: -0.1278, label: 'London (UK)' },
};

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export const RegionMap = ({ data }) => {
  const [tooltip, setTooltip] = useState(null);

  if (!data || !data.providers || data.providers.length === 0) {
    return (
      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="text-lg font-[Outfit]">Carte des régions</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[500px]">
          <p className="text-muted-foreground text-sm">Aucune donnée de région disponible.</p>
        </CardContent>
      </Card>
    );
  }

  // Agréger les services par région
  const regionData = {};
  data.providers.forEach(provider => {
    provider.services?.forEach(service => {
      service.resources?.forEach(resource => {
        if (resource.region) {
          // Normaliser le nom de région
          const normalizedName = resource.region.toLowerCase().trim();

          // Chercher le code de région correspondant
          let regionCode = REGION_NAME_TO_CODE[normalizedName] || normalizedName.replace(/\s+/g, '');

          // Utiliser les coordonnées du code de région
          const coords = REGION_COORDS[regionCode];

          if (!regionData[regionCode]) {
            regionData[regionCode] = {
              region: resource.region,
              provider: provider.provider,
              services: new Set(),
              cost: 0,
              coords: coords
            };
          }
          regionData[regionCode].services.add(service.service);
          regionData[regionCode].cost += resource.cost;
        }
      });
    });
  });

  const markers = Object.values(regionData).filter(r => r.coords);

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-lg font-[Outfit]">Carte des régions</CardTitle>
        <p className="text-xs text-muted-foreground">Services actifs par région géographique</p>
      </CardHeader>
      <CardContent className="relative">
        {markers.length === 0 ? (
          <div className="flex items-center justify-center h-[500px]">
            <p className="text-muted-foreground text-sm">Aucune région géolocalisée disponible.</p>
          </div>
        ) : (
          <>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 120 }}
              width={800}
              height={500}
              className="w-full"
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="hsl(var(--muted))"
                      stroke="hsl(var(--border))"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', fill: 'hsl(var(--muted) / 0.8)' },
                        pressed: { outline: 'none' }
                      }}
                    />
                  ))
                }
              </Geographies>
              {markers.map((marker, i) => (
                <Marker key={i} coordinates={[marker.coords.lon, marker.coords.lat]}>
                  <circle
                    r={Math.min(6 + Math.log(marker.cost + 1) * 2, 12)}
                    fill={PROVIDER_COLORS[marker.provider]}
                    fillOpacity={0.8}
                    stroke="white"
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setTooltip(marker)}
                    onMouseLeave={() => setTooltip(null)}
                  />
                </Marker>
              ))}
            </ComposableMap>

            {/* Tooltip */}
            {tooltip && (
              <div className="absolute top-4 left-4 bg-popover/95 backdrop-blur-sm border border-border shadow-xl rounded-lg p-3 text-sm max-w-xs z-10">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4" style={{ color: PROVIDER_COLORS[tooltip.provider] }} />
                  <span className="font-semibold">{tooltip.coords.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  {tooltip.provider.toUpperCase()}
                </p>
                <p className="font-medium mb-1">
                  {tooltip.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
                <p className="text-xs text-muted-foreground">
                  {tooltip.services.size} service{tooltip.services.size > 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs">
              {Object.entries(PROVIDER_COLORS).map(([provider, color]) => {
                const hasData = markers.some(m => m.provider === provider);
                if (!hasData) return null;
                return (
                  <div key={provider} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-muted-foreground">{provider.toUpperCase()}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
